import { Icon, Tooltip } from '@pms-digital/design-system';

/**
 * The « ou » that sits on the junction of two neighbouring criteria (issue #181,
 * scenario-succes-conduite.md §7; maquette `EditeurObjectifApres.html`, variante A).
 *
 * Two shapes, one gesture each:
 *
 * - `link` - « + ou », on the junction of two lines that are NOT yet one group.
 *   It is drawn only while the junction is hovered or holds the focus: between
 *   two lines nobody points at, nothing is drawn, so the list keeps the density
 *   it had. It takes no room of its own (the wrapper is absolute, straddling the
 *   gap between the two rows), and it is a real button, so the keyboard reaches
 *   it - the focus is the hover's equivalent.
 * - `unlink` - « ou », between two members of a framed group. Always drawn;
 *   hovered or focused it becomes « ou × », which is what says that a press cuts
 *   the group there.
 *
 * A read-only editor keeps the « ou » of an existing group, as a label, and draws
 * no « + ou »: there is no gesture to offer.
 */

export interface OrJointProps {
  kind: 'link' | 'unlink';
  /** The name of the line above and of the line below: they make the accessible name. */
  upperName: string;
  lowerName: string;
  readOnly: boolean;
  onPress: () => void;
}

const PILL =
  'inline-flex h-5.5 items-center gap-1 rounded-full border-2 px-2 font-bold text-micro uppercase leading-none tracking-wide';

export function OrJoint({ kind, upperName, lowerName, readOnly, onPress }: OrJointProps) {
  if (kind === 'link') {
    if (readOnly) return null;
    const label = `Lier ${upperName} et ${lowerName} par « ou »`;
    return (
      // The wrapper is the hover zone: 12 px, centred on the 4 px gap of the list,
      // so it reaches four pixels into each row and no further - the name of a
      // criterion and the buttons of an action row keep their own hit area.
      <div className="group/joint -bottom-2 -left-0.5 absolute z-10 flex h-3 w-24 items-center">
        <Tooltip content={label} side="right">
          <button
            type="button"
            aria-label={label}
            onClick={onPress}
            className={`${PILL} cursor-pointer border-gray-300 border-dashed bg-gray-50 text-gray-600 opacity-0 hover:border-bleu-nuit hover:bg-white hover:text-bleu-nuit focus-visible:border-bleu-nuit focus-visible:bg-white focus-visible:text-bleu-nuit focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit group-hover/joint:opacity-100`}
          >
            <Icon name="add" size={14} />
            ou
          </button>
        </Tooltip>
      </div>
    );
  }

  const label = `Délier ${lowerName} de ${upperName}`;
  return (
    <div className="relative h-3.5">
      {readOnly ? (
        <span
          className={`${PILL} -top-1 -left-0.5 absolute z-10 border-bleu-nuit-tint bg-white text-bleu-nuit`}
        >
          ou
        </span>
      ) : (
        <Tooltip content={label} side="right">
          <button
            type="button"
            aria-label={label}
            onClick={onPress}
            className={`${PILL} group/or -top-1 -left-0.5 absolute z-10 cursor-pointer border-bleu-nuit-tint bg-white text-bleu-nuit hover:border-bleu-nuit focus-visible:border-bleu-nuit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit`}
          >
            ou
            <span className="hidden group-hover/or:inline-flex group-focus-visible/or:inline-flex">
              <Icon name="close" size={14} />
            </span>
          </button>
        </Tooltip>
      )}
    </div>
  );
}
